import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import * as Draggable from '@givebutter/primitives/draggable';
import * as Sortable from '@givebutter/primitives/sortable';
import { useToast } from '@givebutter/primitives/toast';
import { faRectangleHistory, faTicket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaignItems, reorderCampaignItems } from 'api/campaigns';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { handleDragEnd } from 'helpers/handle-drag-end';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignItem } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface OrganizeItemsForm {
    order: number[];
}

const OrganizeItemsFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;

    const params = {
        per_page: 999,
        sortBy: 'order',
        is_auction: false,
    };

    const { data: items } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaign.id, params),
        queryFn: () => fetchCampaignItems({ campaignId: campaign.id, ...params }),
    });

    const formMethods = Form.useForm({
        defaultValues: toForm(items.data),
    });

    const { handleSubmit, setValue, watch } = formMethods;
    const order = watch('order');
    const currentItemTypeText = 'item';

    const { mutate: submit, isPending: isSubmitting } = useMutation({
        mutationFn: (data: OrganizeItemsForm) =>
            reorderCampaignItems(campaignId, toReorderItemsRequest(data)),
        onSuccess: () => {
            toast.success(`Successfully updated ${currentItemTypeText} order!`);
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.itemsFiltered(campaignId, params),
            });
            goBack();
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(
                e.response?.data?.message ?? `Failed to update ${currentItemTypeText} order`,
            ),
    });

    let position = 0;

    return (
        <Form.Provider {...formMethods}>
            <form
                onSubmit={handleSubmit(submit as SubmitHandler<OrganizeItemsForm>)}
                className={'contents'}
            >
                <Drawer.Header>
                    <Drawer.Title>Organize</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body className={'flex flex-col gap-y-6'}>
                    <section>
                        <h2 className={'text-base font-medium'}>Sort and order your items</h2>
                        <p className={'text-sm leading-5 text-gray-600'}>
                            Hover over a ticket or bundle and drag to edit the order of{' '}
                            {`${currentItemTypeText}s`} on your campaign page.
                        </p>
                    </section>

                    <section className={'flex flex-col gap-y-1'}>
                        <Draggable.Root
                            onDragEnd={event => {
                                setValue('order', handleDragEnd(event, order));
                            }}
                        >
                            <Sortable.Root items={order}>
                                {order.map(itemId => {
                                    const item = items.data.find(i => i.id === itemId);

                                    if (!item) {
                                        return null;
                                    }

                                    return (
                                        <Sortable.Item
                                            key={item.id}
                                            itemId={item.id}
                                            className={
                                                'data-[dragging=true]:z-outermost flex items-baseline gap-x-2 focus:outline-2 focus:outline-blue-500'
                                            }
                                        >
                                            <>
                                                <div className={'flex w-6 justify-end'}>
                                                    {item.active && (
                                                        <p className={'text-gray-600'}>
                                                            {++position}.
                                                        </p>
                                                    )}
                                                </div>
                                                <Sortable.Card>
                                                    <Sortable.Grip />
                                                    <FontAwesomeIcon
                                                        icon={
                                                            item.subtype === 'bundle'
                                                                ? faRectangleHistory
                                                                : faTicket
                                                        }
                                                        className={`${
                                                            item.active
                                                                ? 'text-gray-400'
                                                                : 'text-gray-300'
                                                        }`}
                                                    />
                                                    <p
                                                        className={`line-clamp-1 ${
                                                            item.active ? '' : 'text-gray-300'
                                                        }`}
                                                    >
                                                        {item.name}
                                                    </p>
                                                    {item.item_status === 'sold_out' && (
                                                        <Badge size="sm" color="error">
                                                            Sold out
                                                        </Badge>
                                                    )}
                                                    {item.item_status !== 'sold_out' &&
                                                        item.bundle_only && (
                                                            <Badge size="sm" color="primary">
                                                                Bundle only
                                                            </Badge>
                                                        )}
                                                </Sortable.Card>
                                            </>
                                        </Sortable.Item>
                                    );
                                })}
                            </Sortable.Root>
                        </Draggable.Root>
                    </section>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton isSubmitting={isSubmitting}>Save</Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const toForm = (items: CampaignItem[]) => ({
    order: items.map(item => item.id),
});

const toReorderItemsRequest = (data: OrganizeItemsForm) => ({
    order: data.order.join(','),
});

export { OrganizeItemsFormDrawer };
